import React from 'react';
import { X, Clock, User, Calendar, BookOpen, CheckCircle, XCircle } from 'lucide-react';
import type { BetaApplication } from '../../../types';

interface ApplicationModalProps {
  isOpen: boolean;
  onClose: () => void;
  application: BetaApplication;
  onUpdateStatus?: (status: 'approved' | 'rejected') => Promise<void>;
}

export default function ApplicationModal({
  isOpen,
  onClose,
  application,
  onUpdateStatus
}: ApplicationModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full">
          <div className="flex items-center justify-between border-b px-6 py-4">
            <h2 className="text-xl font-bold">Beta Reader Application</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="p-6">
            {/* Book and Author Info */}
            <div className="mb-6">
              <div className="flex items-center gap-2 text-gray-600 mb-2">
                <BookOpen className="w-5 h-5" />
                <span className="font-medium">{application.book_ideas.title}</span>
              </div>
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <div className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  <span>By {application.author.full_name}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  <span>Applied {new Date(application.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            </div>

            {/* Application Status */}
            <div className="mb-6">
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm ${
                application.status === 'approved'
                  ? 'bg-green-100 text-green-800'
                  : application.status === 'rejected'
                  ? 'bg-red-100 text-red-800'
                  : 'bg-yellow-100 text-yellow-800'
              }`}>
                {application.status === 'approved' ? (
                  <CheckCircle className="w-4 h-4" />
                ) : application.status === 'rejected' ? (
                  <XCircle className="w-4 h-4" />
                ) : (
                  <Clock className="w-4 h-4" />
                )}
                <span className="font-medium">
                  {application.status.charAt(0).toUpperCase() + application.status.slice(1)}
                </span>
              </div>
            </div>

            {/* Application Details */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-2">Motivation</h3>
                <p className="text-gray-700 whitespace-pre-wrap">{application.motivation}</p>
              </div>

              {application.completion_weeks && (
                <div>
                  <h3 className="text-lg font-semibold mb-2">Estimated Completion Time</h3>
                  <p className="text-gray-700">
                    {application.completion_weeks} {application.completion_weeks === 1 ? 'week' : 'weeks'}
                  </p>
                </div>
              )}

              {application.preferred_genres && application.preferred_genres.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-2">Preferred Genres</h3>
                  <div className="flex flex-wrap gap-2">
                    {application.preferred_genres.map((genre) => (
                      <span
                        key={genre}
                        className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                      >
                        {genre}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {application.critique_areas && application.critique_areas.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-2">Areas of Focus</h3>
                  <div className="flex flex-wrap gap-2">
                    {application.critique_areas.map((area) => (
                      <span
                        key={area}
                        className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                      >
                        {area}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {application.additional_info && (
                <div>
                  <h3 className="text-lg font-semibold mb-2">Additional Information</h3>
                  <p className="text-gray-700 whitespace-pre-wrap">{application.additional_info}</p>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            {onUpdateStatus && application.status === 'pending' && (
              <div className="flex justify-end gap-4 mt-8 pt-6 border-t">
                <button
                  onClick={() => onUpdateStatus('rejected')}
                  className="px-4 py-2 text-red-600 hover:text-red-700"
                >
                  Reject
                </button>
                <button
                  onClick={() => onUpdateStatus('approved')}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Approve
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}